import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * The layer the app shell keeps for what opens over the page (the phone's navigation drawer, a slide-out panel). While
 * anything is open there the rest of the page is `inert`: focus and the screen reader stay in the overlay. When the
 * last one closes, and only once the page has taken the inert off (orno's drawer gives focus back while the page is
 * still inert, which cannot take it), focus goes back to what opened it.
 */
export interface OverlayLayer {
  /** Where an overlay is rendered: beside the page, never inside what turns inert; null until it is on the page. */
  host: HTMLElement | null;
  /** An overlay opened; the answer is to call when it closes. `returnFocus` takes focus back then. */
  enter: (returnFocus: HTMLElement | null) => () => void;
}

const OverlayContext = createContext<OverlayLayer | null>(null);

export const OverlayProvider = OverlayContext.Provider;

/**
 * The app shell's side of the layer: the context's value, a ref for the element overlays render into, and whether the
 * page is inert now.
 */
export function useOverlayLayer(): {
  layer: OverlayLayer;
  hostRef: (element: HTMLElement | null) => void;
  inert: boolean;
} {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(0);
  // The last overlay that closed, and what to give focus back to (null: nothing opened it, as a panel in the address).
  const closed = useRef<{ returnFocus: HTMLElement | null } | null>(null);

  const enter = useCallback((returnFocus: HTMLElement | null) => {
    setOpen((count) => count + 1);
    return () => {
      closed.current = { returnFocus };
      setOpen((count) => count - 1);
    };
  }, []);

  // Runs after React took the inert off. Without an opener, or one that left the page (a deleted row), the main
  // content takes focus.
  useEffect(() => {
    if (open > 0 || closed.current === null) return;
    const target = closed.current.returnFocus;
    closed.current = null;
    if (target?.isConnected) target.focus();
    else document.getElementById('main')?.focus();
  }, [open]);

  const layer = useMemo(() => ({ host, enter }), [host, enter]);
  return { layer, hostRef: setHost, inert: open > 0 };
}

/**
 * Marks the page under an overlay inert while `open`, and gives focus back to `returnFocus` once it closes. Without
 * an app shell (a component under test) there is no inert to wait for: focus goes back at once.
 */
export function useOverlay(open: boolean, returnFocus: HTMLElement | null): void {
  const layer = useContext(OverlayContext);
  const enter = layer?.enter;
  useEffect(() => {
    if (!open) return;
    if (enter) return enter(returnFocus);
    return () => {
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, [open, enter, returnFocus]);
}

/**
 * Something that opens over the page, such as a slide-out panel: rendered beside the page (so the page can turn inert
 * without it), the page inert while it is mounted, and focus back to `returnFocus` when it goes.
 */
export function Overlay({ returnFocus, children }: { returnFocus: HTMLElement | null; children: ReactNode }) {
  const layer = useContext(OverlayContext);
  useOverlay(true, returnFocus);
  if (layer === null) return children;
  return layer.host === null ? null : createPortal(children, layer.host);
}
